<template>
    <div>
        <VJoyWrapper no-margin direction="column" align="flex-start" row-gap="3">
            <VJoyWrapper
                v-for="(item, index) in r$.languages.$each"
                :key="item.$id"
                class="profile-languages-edition-section__wrapper"
                no-margin
                direction="column"
                align="flex-start"
                row-gap="3"
            >
                <VJoyWrapper class="profile-languages-edition-section__item" no-margin direction="row" wrap="nowrap" column-gap="4">
                    <SelectLanguage
                        :id="index"
                        class="profile-languages-edition-section__item-language"
                        :language="getLocalizedLanguageName(item.$value.code)"
                        :invalid="item.$error"
                        @language-updated="selectLanguage($event.code, item.$id)"
                    />
                    <VJoySelect v-model="item.$value.level" class="profile-languages-edition-section__item-level">
                        <option disabled :value="null">
                            {{ t('profile.creation.completion.wizard.languages.level') }}
                        </option>
                        <option v-for="languageLevel in availableLanguagesLevel" :key="`${languageLevel.level}`" :value="languageLevel.level">
                            {{ languageLevel.text }}
                        </option>
                    </VJoySelect>
                    <VJoyIconButton
                        v-if="index > 0"
                        class="profile-languages-edition-section__item-button"
                        :data-testid="`profile-languages--delete-button-${index}`"
                        size="small"
                        icon="trash"
                        variant="ghost"
                        :label="$t('general.delete')"
                        @click="removeLanguage(item.$id)"
                    />
                    <div v-else class="profile-languages-edition-section__item-button" />
                </VJoyWrapper>
                <div v-if="r$.languages.$each[index].$error" class="profile-languages-edition-section__error">
                    <VJoyFormError
                        v-for="error of r$.$errors.languages.$each[index].code"
                        :key="error"
                        class="profile-languages-edition-section__error"
                        no-margin
                    >
                        {{ error }}
                    </VJoyFormError>
                </div>
            </VJoyWrapper>
            <div v-if="r$.languages.$error" class="profile-languages-edition-section__error">
                <VJoyFormError v-for="(error, index) of r$.$errors.languages.$self" :key="index" no-margin>
                    {{ error }}
                </VJoyFormError>
            </div>
            <VJoyButton :disabled="isAddButtonDisabled" icon="add" variant="secondary" @click="addLanguage">
                {{ t('profile.creation.completion.wizard.v2.languages.action.add') }}
            </VJoyButton>
        </VJoyWrapper>
    </div>
</template>
<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {maxLength, minLength, required, withMessage} from '@regle/rules';
    import {storeToRefs} from 'pinia';
    import {useLanguageFormatter, useTranslation} from '#imports';
    import {VJoyButton, VJoyIconButton, VJoyFormError, VJoySelect, VJoyWrapper} from '@maltjoy/core-vue';
    import type {LanguageLevel} from '@api';
    import SelectLanguage from '~/components/edit/SelectLanguage.vue';
    import {type LanguageEditionItem, toLanguageEditionItem, useScopedRegleItem} from '~/features/about/types/EditAboutSection';
    import {useProfileStore} from '~/store/profileStore';
    import {useRulesStore} from '~/store/rules/rulesStore';

    const {t} = useTranslation();
    const {getLocalizedLanguageName} = useLanguageFormatter();

    const {profile} = storeToRefs(useProfileStore());
    const {languageCount} = storeToRefs(useRulesStore());

    const availableLanguagesLevel = [
        {level: 'NATIVE', text: t('profile.new.language.level.NATIVE')},
        {level: 'FLUENT', text: t('profile.new.language.level.FLUENT')},
        {level: 'CONVERSATIONAL', text: t('profile.new.language.level.CONVERSATIONAL')},
        {level: 'BASIC', text: t('profile.new.language.level.BASIC')},
    ];

    const isAddButtonDisabled = computed(() => state.value.languages.length >= languageCount.value.max);
    const rules = computed(() => ({
        languages: {
            minLength: withMessage(
                minLength(languageCount.value.min),
                t('profile.section.languages.edit.error.min', {count: languageCount.value.min}),
            ),
            maxLength: withMessage(maxLength(languageCount.value.max), t('profile.section.languages.edit.error.max', {0: languageCount.value.max})),
            $each: {
                code: {
                    required,
                    hasNoDuplicates: withMessage(
                        (value) =>
                            state.value.languages
                                .filter((item) => item.code && item.code.length > 0)
                                .filter((item) => item.code.toLowerCase() === (value as string).toLowerCase()).length < 2,
                        t('profile.section.languages.edit.error.duplicated'),
                    ),
                },
            },
        },
    }));
    const defaultState = computed(() => ({
        languages: profile.value?.languages.items.map((item) => toLanguageEditionItem(item)) ?? [],
    }));
    const state = ref({...defaultState.value});
    const {r$} = useScopedRegleItem(state, rules, {id: 'languagesEdition'});

    function addLanguage() {
        const newLanguage: LanguageEditionItem = {
            name: '',
            code: '',
            level: 'FLUENT' as LanguageLevel,
        };
        state.value.languages = [...state.value.languages, newLanguage];
    }

    function selectLanguage(selectedLanguageCode: string, id: string | undefined) {
        state.value.languages = r$.languages.$each.map((item) => {
            if (item.$id === id) {
                return {
                    ...item.$value,
                    code: selectedLanguageCode.toLowerCase(),
                };
            }
            return {
                ...item.$value,
            };
        });
    }

    function removeLanguage(idToRemove: string | undefined) {
        state.value.languages = r$.languages.$each.filter((item) => item.$id !== idToRemove).map((item) => item.$value);
        r$.$reset();
    }
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .profile-languages-edition-section {
        &__wrapper {
            width: 100%;
        }

        &__item {
            width: 100%;
        }

        &__item-language {
            flex: 0 0 45%;

            @include mq.screen_xs {
                flex: 0 0 40%;
            }
        }

        &__item-level {
            flex: 0 0 40%;

            @include mq.screen_xs {
                flex: 0 0 35%;
            }
        }

        &__item-button {
            height: 48px;
            flex: 0 0 48px;

            @include mq.screen_xs {
                flex: 0 0 30px;
            }
        }
    }
</style>
